import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import type { PackageDomainListDto } from '../models/packages/package-domain-list.dto';
import type { WebsiteContactInfoDto } from '../models/website-content/website-content.models';

@Injectable({ providedIn: 'root' })
export class StructuredDataService {
  private readonly document = inject(DOCUMENT);

  setOrganization(): void {
    this.setJsonLd('organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'WorkNest',
      logo: `${environment.siteUrl}/assets/WORKNEST-ICON.display.png`,
      url: environment.siteUrl,
    });
  }

  setPackageOffers(packages: PackageDomainListDto[]): void {
    const active = packages.filter((p) => p.isActive);
    if (active.length === 0) {
      return;
    }

    this.setJsonLd('packages', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: active.map((pkg, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: pkg.title,
          description: pkg.description ?? undefined,
          offers: {
            '@type': 'Offer',
            price: pkg.price,
            priceCurrency: pkg.currency ?? 'USD',
            availability: 'https://schema.org/InStock',
          },
        },
      })),
    });
  }

  setContactPage(contact: WebsiteContactInfoDto): void {
    this.setJsonLd('contact', {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      mainEntity: {
        '@type': 'Organization',
        name: 'WorkNest',
        telephone: contact.phoneNumber,
        email: contact.email,
        address: contact.address,
      },
    });
  }

  clear(id: string): void {
    this.document.querySelector(`script[data-seo-id="${id}"]`)?.remove();
  }

  private setJsonLd(id: string, data: unknown): void {
    let script = this.document.querySelector<HTMLScriptElement>(`script[data-seo-id="${id}"]`);
    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-id', id);
      this.document.head.appendChild(script);
    }
    // Escape `<` (as the equivalent <) so CMS-authored strings containing
    // a literal `</script>` can never break out of this script tag once the
    // JSON-LD is serialized into static prerendered HTML.
    script.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
  }
}
